import React from 'react';

/**
 * Component that renders the terms and conditions content for company users.
 * Displays the complete Azymmetric Tyr Platform Terms and Conditions Agreement.
 */
export const CompanyTermsContent: React.FC = () => {
  return (
    <div className="max-w-none space-y-6 text-sm">
      <h2 className="text-xl font-bold text-center">AZYMMETRIC TYR PLATFORM TERMS AND CONDITIONS AGREEMENT</h2>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">1. ACCEPTANCE OF TERMS</h3>
        <p>
          By clicking "I Agree" and accessing or using the Azymmetric Tyr platform, you ("User," "Company," or "you")
          acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions ("Terms") and
          all applicable laws and regulations. This agreement is effective as of the date you first access the Tyr
          platform and constitutes a legally binding contract between you and Azymmetric Inc, a Texas C-Corp company
          ("Azymmetric," "we," "us," or "our").
        </p>
        <p>If you do not agree to these Terms, you must not access or use the Azymmetric Tyr platform.</p>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">2. PLATFORM DESCRIPTION AND SERVICES</h3>
        <p>
          Azymmetric operates the Tyr Software as a Service (SaaS) platform that provides Technology Readiness Level
          (TRL) and Manufacturing Readiness Level (MRL) assessments using both artificial intelligence technologies and
          human expert analysis. The Tyr platform collects subsystem data and provides expert analysis from military
          veterans and technology experts regarding technology-mission fit recommendations through AI-assisted analysis,
          automated processing, and human expert review. Tyr functions as an open source intelligence (OSINT) platform
          designed to serve defense and technology sectors through the combined expertise of artificial intelligence
          systems and qualified human professionals.
        </p>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">3. HUMAN EXPERT ANALYSIS AND AI-ENHANCED SERVICES</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.1 Combined AI and Human Expert Analysis</h4>
          <p>
            The Tyr platform utilizes both artificial intelligence technologies and human expert analysis to provide
            comprehensive assessments, recommendations, and insights. Human experts, including military veterans and
            technology specialists, work in conjunction with AI systems to deliver enhanced analysis. All
            recommendations combine AI-generated content with human expert review and professional judgment.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.2 Expert Qualifications and Credentials</h4>
          <p>
            Azymmetric's human experts consist of qualified military veterans with relevant defense experience and
            technology experts with specialized knowledge in their respective fields. Expert credentials and
            qualifications are verified by Azymmetric, and expert analysis is provided based on professional experience
            and domain expertise combined with AI-assisted tools.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.3 AI-Enhanced Expert Content</h4>
          <p>
            AI-generated content from the Tyr platform is reviewed and enhanced by human experts to provide more
            accurate and contextually relevant assessments. However, all content, whether AI-generated, expert-provided,
            or combined, is provided for informational purposes only and should not be relied upon as the sole basis for
            business decisions.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.4 Expert Analysis Accuracy Disclaimer</h4>
          <p>
            While human experts bring professional judgment and experience to the analysis process, expert opinions and
            AI-generated content may still contain errors, inaccuracies, or incomplete information. Companies must
            independently verify all expert recommendations and AI-generated content from Tyr and exercise independent
            judgment before making any business decisions based on such analysis.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">4. ARTIFICIAL INTELLIGENCE USAGE AND DISCLAIMERS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.1 AI-Generated Content and Analysis</h4>
          <p>
            The Tyr platform utilizes artificial intelligence, machine learning algorithms, and automated analysis tools
            to support human expert analysis and provide assessments, recommendations, and insights. AI systems work in
            conjunction with human experts to enhance the quality and scope of analysis provided through the platform.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.2 AI Content Accuracy Disclaimer</h4>
          <p>
            AI-generated content from the Tyr platform may contain errors, inaccuracies, or incomplete information due
            to the inherent limitations of artificial intelligence technology. Users acknowledge that AI systems are not
            infallible and may produce outputs that are incorrect, biased, or misleading. Companies must independently
            verify all AI-generated content from Tyr and exercise human judgment before making any business decisions
            based on such content.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.3 Machine Learning Liability Limitations</h4>
          <p>
            Azymmetric disclaims all liability for decisions made by machine learning algorithms or AI systems
            integrated into the Tyr platform. The complexity and "black box" nature of certain AI technologies may make
            it impossible to fully explain or predict specific outcomes. Users acknowledge that AI decision-making
            processes may be opaque and that liability for AI-generated errors or recommendations cannot be attributed
            to Azymmetric.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.4 Human Oversight and Review</h4>
          <p>
            AI technologies are designed to assist and enhance human expert analysis through Tyr. All AI outputs are
            subject to potential human expert review and oversight. Companies are responsible for implementing
            appropriate independent verification processes when using AI-generated content and expert recommendations
            from the Tyr platform.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">5. CONFIDENTIAL AND CLASSIFIED INFORMATION PROHIBITION</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.1 Strict Prohibition on Classified Information</h4>
          <p>
            <span className="uppercase font-semibold">
              Companies are strictly prohibited from entering, uploading, or submitting any classified information,
              controlled unclassified information (CUI), or information subject to export control regulations including
              ITAR or EAR to the Tyr platform.
            </span>
            <span>
              This prohibition includes but is not limited to information classified at any level under U.S. government
              classification systems, information controlled under the International Traffic in Arms Regulations (ITAR),
              Export Administration Regulations (EAR), or similar international export control regimes.
            </span>
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.2 Intellectual Property and Confidential Information Restrictions</h4>
          <p>
            Companies must not submit proprietary intellectual property, trade secrets, confidential business
            information, or any information subject to non-disclosure agreements to the Tyr platform unless they have
            explicit authorization to do so. This includes but is not limited to proprietary algorithms, source code,
            detailed technical specifications, manufacturing processes, financial data, customer information, and
            strategic business plans.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.3 Sole Liability for Prohibited Information Submission</h4>
          <p>
            <span className="uppercase font-semibold">
              If any company submits classified information, controlled information, confidential information, or
              intellectual property to the Tyr platform, such submission shall be the sole liability and responsibility
              of the submitting company.
            </span>
            <span>
              The company acknowledges that it will be fully responsible for any and all consequences, damages,
              penalties, legal actions, or regulatory violations arising from such unauthorized submission.
            </span>
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.4 Immediate Termination for Violations</h4>
          <p>
            Azymmetric reserves the right to immediately terminate access to Tyr and report any suspected submission of
            classified or controlled information to appropriate authorities. Companies found to have violated these
            restrictions may face immediate account termination, legal action, and potential reporting to relevant
            government agencies.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">6. DATA OWNERSHIP AND RIGHTS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">6.1 Azymmetric Data Ownership</h4>
          <p>
            Azymmetric owns and retains all rights, title, and interest in and to all data collected, processed, stored,
            or generated through the Tyr platform, including but not limited to assessment results, analytics,
            aggregated data, AI-generated insights, expert analysis, and machine learning model outputs. This includes
            all intellectual property rights in such data, AI-generated content, and expert-provided analysis.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">6.2 Company-Specific Data Exception</h4>
          <p>
            Notwithstanding Section 6.1, companies retain ownership of non-confidential data that specifically relates
            to their general business information submitted to the Tyr platform, provided such information does not
            violate the restrictions in Section 5. However, Azymmetric retains the right to use such data in aggregated,
            anonymized form for Tyr platform improvement, AI model training, expert analysis enhancement, and analytical
            purposes.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">6.3 AI Training and Expert Enhancement Rights</h4>
          <p>
            By using the Tyr platform, you grant Azymmetric a perpetual, irrevocable, worldwide, royalty-free license to
            use, process, analyze, and derive insights from all permissible data for AI model training, machine learning
            algorithm improvement, expert analysis enhancement, and platform development.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">7. TRUTH STATEMENTS AND LEGAL LIABILITY</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">7.1 Accuracy Requirement</h4>
          <p>
            Companies must provide accurate, complete, and truthful information in all assessments, submissions, and
            communications with the Tyr platform. You represent and warrant that all information provided is true,
            correct, and not subject to confidentiality restrictions to the best of your knowledge.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">7.2 Misrepresentation Liability</h4>
          <p>
            Any misrepresentation, false statement, or fraudulent information provided by a company may result in
            immediate termination of Tyr access and legal liability. Companies acknowledge that they may be held legally
            responsible for any damages, losses, or consequences resulting from false or misleading information,
            including AI-generated outputs and expert analysis based on such false information.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">7.3 Indemnification for False Statements</h4>
          <p>
            Companies agree to indemnify and hold harmless Azymmetric from any claims, damages, losses, or expenses
            arising from false, inaccurate, or misleading information provided by the company, including any
            consequences of AI analysis or expert recommendations based on such information.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">8. EXPERT RECOMMENDATIONS AND LIABILITY LIMITATIONS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.1 Advisory Nature of Expert and AI Recommendations</h4>
          <p>
            All recommendations, assessments, and guidance provided by the Tyr platform's AI systems, military veteran
            experts, and technology experts are advisory in nature and should not be considered as professional advice
            for specific business decisions. Expert recommendations are based on professional judgment, available data,
            and AI-assisted analysis but do not guarantee specific outcomes or accuracy.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.2 No Liability for Expert or AI-Generated Actions</h4>
          <p>
            Azymmetric expressly disclaims any liability for actions taken or not taken by companies based on expert
            recommendations, AI-generated content, machine learning outputs, or Tyr platform assessments. Companies
            acknowledge that they are solely responsible for their business decisions and any consequences thereof,
            whether based on expert analysis, AI-generated content, or combined recommendations from Tyr.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.3 Expert and AI System Limitations</h4>
          <p>
            Users acknowledge that both human experts and AI systems within Tyr may have limitations, including
            potential bias in expert judgment, errors in professional analysis, or AI systems that may produce
            discriminatory outputs or make decisions based on incomplete information. Azymmetric does not warrant that
            expert analysis or AI-generated content will be free from bias, errors, or limitations inherent in human
            judgment or machine learning technologies.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.4 Enhanced Limitation of Liability for Combined Services</h4>
          <p className="font-semibold uppercase">
            To the maximum extent permitted by Texas law, Azymmetric's total liability for any claims arising from or
            related to expert recommendations, AI-generated content, machine learning outputs, or automated tyr platform
            decisions shall not exceed the amount paid by the company to Azymmetric in the twelve (12) months preceding
            the event giving rise to the claim. Azymmetric shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages arising from expert analysis errors, AI system errors, or limitations in
            combined AI and human analysis.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">9. GLOBAL DATA PRIVACY AND AI COMPLIANCE</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">9.1 Multi-Jurisdictional AI Compliance</h4>
          <p>
            Azymmetric is committed to complying with applicable data protection laws and AI regulations in all
            jurisdictions where the Tyr platform operates, including but not limited to the European Union General Data
            Protection Regulation (GDPR), EU AI Act, California Consumer Privacy Act (CCPA), Australian Privacy
            Principles (APPs), UK Data Protection Act, and Japan's Act on the Protection of Personal Information (APPI).
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">9.2 AI-Specific Regulatory Compliance</h4>
          <p>
            Azymmetric implements measures to comply with emerging AI regulations, including transparency requirements,
            algorithmic accountability standards, and AI system documentation as required by applicable law. Users
            acknowledge that AI regulatory frameworks are evolving and may impact Tyr platform operations.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">9.3 Cross-Border Data Transfers and AI Processing</h4>
          <p>
            Data may be transferred to and processed in the United States and other jurisdictions as necessary for AI
            model training, machine learning operations, expert analysis, and Tyr platform functionality. Azymmetric
            implements appropriate safeguards for international data transfers involving AI processing as required by
            applicable law.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">9.4 AI Data Retention and Processing</h4>
          <p>
            Azymmetric retains data for AI model training, expert analysis improvement, and platform enhancement
            purposes as long as necessary to fulfill the purposes outlined in these Terms and as required by applicable
            AI regulations. Users may request deletion of personal data subject to legal, operational, and AI system
            requirements.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">10. TEXAS LAW COMPLIANCE AND AI GOVERNANCE</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">10.1 Governing Law</h4>
          <p>
            These Terms are governed by and construed in accordance with the laws of the State of Texas, without regard
            to conflict of law principles. Any disputes arising under these Terms, including those related to expert
            analysis, AI system performance, or AI-generated content, shall be subject to the exclusive jurisdiction of
            the courts of Texas.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">10.2 AI Technology Legal Framework</h4>
          <p>
            Azymmetric operates AI systems in compliance with applicable Texas and federal regulations governing
            artificial intelligence, machine learning, and automated decision-making systems. Users acknowledge that AI
            legal frameworks are evolving and may require updates to Tyr platform operations.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">10.3 Electronic Agreement Validity</h4>
          <p>
            This agreement is executed electronically and is valid and enforceable under the Texas Uniform Electronic
            Transactions Act. Your click-through acceptance constitutes a legally binding signature for all terms,
            including AI-related and expert analysis provisions.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">11. PLATFORM ACCESS AND AI SECURITY</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">11.1 Authorized Use of AI Systems and Expert Services</h4>
          <p>
            Users may only access AI-powered Tyr platform features and expert analysis services for legitimate business
            purposes related to technology and defense assessments. Any unauthorized use, including attempts to reverse
            engineer AI algorithms, circumvent AI security measures, manipulate AI outputs, or misrepresent information
            to experts, is strictly prohibited.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">11.2 AI Export Control Compliance</h4>
          <p>
            Use of the Tyr platform's AI capabilities and expert analysis must comply with all applicable export control
            laws and regulations, including restrictions on AI technology transfers under EAR and ITAR. Users are
            responsible for ensuring their use of content, expert recommendations, and AI systems is lawful in their
            jurisdiction.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">11.3 AI Security Measures</h4>
          <p>
            Azymmetric implements reasonable security measures to protect AI systems, algorithms, expert analysis, and
            AI-generated data within the Tyr platform. However, users acknowledge the inherent risks of AI technology,
            including potential security vulnerabilities in machine learning systems.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">12. INTELLECTUAL PROPERTY AND AI PROTECTION</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">12.1 AI Platform and Expert Analysis Intellectual Property</h4>
          <p>
            All intellectual property rights in the Tyr platform, including AI algorithms, machine learning models,
            training data, AI-generated content templates, expert analysis methodologies, and AI system architectures,
            are owned by Azymmetric. Users are granted a limited, non-exclusive license to use AI-generated Tyr platform
            outputs and expert analysis solely for their intended business purposes.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">12.2 AI-Generated Content and Expert Analysis Ownership</h4>
          <p>
            Azymmetric retains ownership of AI algorithms, models, and expert analysis methodologies, while users may
            have limited rights to AI-generated outputs and expert recommendations created specifically for their use
            through Tyr, subject to the restrictions in these Terms. Users represent that they have the right to submit
            all information to AI systems and human experts on the Tyr platform.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">12.3 Protection of AI and Expert Trade Secrets</h4>
          <p>
            Users acknowledge that AI algorithms, machine learning models, AI system architectures, and expert analysis
            methodologies constitute valuable trade secrets and proprietary information of Azymmetric. Any unauthorized
            disclosure or reverse engineering of Tyr AI systems or expert processes is strictly prohibited.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">13. DEFENSE AND MILITARY AI CONTRACTOR COMPLIANCE</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">13.1 AI Systems and Controlled Information</h4>
          <p>
            Users handling AI-generated assessments and expert analysis involving defense-related information must
            comply with applicable regulations including NIST 800-171, DFARS, and CMMC requirements.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">13.2 AI Export Control Regulations</h4>
          <p>
            Users must ensure compliance with ITAR, EAR, and other export control regulations when using AI-generated
            content, expert recommendations, or AI analysis capabilities from Tyr. Azymmetric is not responsible for
            users' export control compliance related to AI technology usage or expert analysis.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">13.3 AI System Security Requirements</h4>
          <p>
            AI systems and expert analysis processes handling defense-related information implement security controls
            consistent with federal cybersecurity frameworks and AI security standards. Users acknowledge additional
            security requirements may apply to AI systems and expert processes handling sensitive information.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">
          14. THIRD-PARTY LIABILITY PROTECTION FOR AI SYSTEMS AND EXPERT ANALYSIS
        </h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">14.1 Third-Party AI Content and Expert Analysis Reliance Disclaimer</h4>
          <p>
            Third parties who rely on AI-generated data, expert recommendations, assessments, or analysis from the Tyr
            platform do so at their own risk. Azymmetric makes no representations or warranties regarding the accuracy,
            reliability, or suitability of AI-generated information or expert analysis for third-party use.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">14.2 AI System and Expert Analysis Indemnification by Users</h4>
          <p>
            Users agree to indemnify and hold harmless Azymmetric from any claims by third parties arising from the
            user's use of AI-generated Tyr platform content, expert recommendations, or reliance on AI system outputs or
            expert analysis. This indemnification extends to legal fees and costs associated with defending AI-related
            or expert analysis liability claims.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">14.3 No Third-Party AI or Expert Beneficiaries</h4>
          <p>
            These Terms do not create any third-party beneficiary rights related to AI systems, expert analysis, or
            AI-generated content, and no third parties may enforce any AI-related or expert analysis provisions of this
            agreement.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">15. TERMINATION AND SURVIVAL</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">15.1 Termination Rights</h4>
          <p>
            Either party may terminate access to the Tyr platform with thirty (30) days written notice. Azymmetric may
            terminate access immediately for breach of these Terms, illegal use, or violation of confidential
            information restrictions.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">15.2 Survival of AI-Related and Expert Analysis Terms</h4>
          <p>
            Sections relating to AI-generated content disclaimers, expert analysis limitations, confidential information
            restrictions, data ownership, liability limitations, and indemnification shall survive termination of this
            agreement.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">15.3 AI Data and Expert Analysis Retention Post-Termination</h4>
          <p>
            Upon termination, Azymmetric may retain data necessary for AI model improvement, expert analysis
            enhancement, and Tyr platform development as permitted by law. Users' rights to AI-generated content and
            expert analysis are subject to the surviving provisions of these Terms.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">16. DISPUTE RESOLUTION</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">16.1 Mediation Requirement</h4>
          <p>
            Before initiating litigation, parties agree to attempt resolution through mediation conducted in Texas. This
            includes disputes related to AI system performance, expert analysis quality, AI-generated content accuracy,
            or confidential information violations.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">16.2 Statute of Limitations for AI and Expert Claims</h4>
          <p>
            Any claims arising under these Terms, including those related to AI system errors, expert analysis errors,
            or AI-generated content, must be brought within four (4) years of the date the claim accrues, as permitted
            under Texas law.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">17. MODIFICATIONS AND AMENDMENTS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">17.1 Amendment Rights for AI and Expert Updates</h4>
          <p>
            Azymmetric reserves the right to modify these Terms at any time, including updates related to AI system
            capabilities, expert analysis processes, AI regulatory compliance, or AI-generated content policies. Updated
            terms will be posted on the Tyr platform, and continued use constitutes acceptance.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">17.2 AI Regulatory and Expert Standards Updates</h4>
          <p>
            Material changes to AI-related or expert analysis terms will be communicated to users through the Tyr
            platform or via email, particularly those related to new AI regulations, expert qualification requirements,
            or AI system modifications.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">18. MISCELLANEOUS PROVISIONS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">18.1 Severability</h4>
          <p>
            If any provision of these Terms, including AI-related or expert analysis clauses, is found to be
            unenforceable, the remaining provisions shall remain in full force and effect.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">18.2 Entire Agreement</h4>
          <p>
            These Terms constitute the entire agreement between the parties regarding Tyr platform use, AI services,
            expert analysis, and confidential information handling, superseding all prior agreements or understandings.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">18.3 Force Majeure</h4>
          <p>
            Neither party shall be liable for delays or failures in performance due to causes beyond their reasonable
            control, including AI system outages, expert availability issues, or AI technology failures.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">18.4 Assignment</h4>
          <p>
            Users may not assign their rights under these Terms without Azymmetric's written consent. Azymmetric may
            assign its rights freely, including AI-related intellectual property, expert analysis rights, and data
            rights.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">19. FINAL ACKNOWLEDGMENT AND ACCEPTANCE</h3>
        <p>
          By clicking "I Agree," you acknowledge that you have read, understood, and agree to be bound by these Terms
          and Conditions, including all AI-related provisions, expert analysis limitations, and confidential information
          restrictions. You specifically acknowledge understanding that:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>
            AI-generated content and expert analysis may contain errors and should not be relied upon without
            independent verification
          </li>
          <li>
            Submission of classified or confidential information is strictly prohibited and will result in sole
            liability for the submitting company
          </li>
          <li>
            AI systems and human experts have inherent limitations and Azymmetric disclaims liability for AI-generated
            or expert analysis errors
          </li>
          <li>These terms comply with applicable AI regulations and Texas state law</li>
        </ul>
        <p>
          This agreement is effective immediately upon your acceptance and continues until terminated in accordance with
          these Terms.
        </p>
      </section>

      <div className="space-y-1 text-xs text-gray-500">
        <p>Last Updated: 15 June 2025</p>
        <p>Azymmetric Inc.</p>
        <p>Governed by Texas Law</p>
        <p>Tyr by Azymmetric Inc.</p>
      </div>
    </div>
  );
};
